const api = import.meta.env.VITE_API_URL;

const BASE_URL = `${api}/api/v1`;

import { getData } from '../services/FetchNodeServices.js';

// ─── Types ────────────────────────────────────────────────────────────────────

/** What we send to the API (camelCase, matches controller destructuring) */
export interface ItemPayload {
  name: string;
  code?: string;
  barcode?: string;
  categoryId?: string;
  categoryName?: string;
  brand?: string;
  hsnCode?: string;
  hsnDescription?: string | null;
  hsnVerified?: boolean;
  taxRate?: number;
  unitId?: string;
  unitName?: string;
  purchaseRate?: number;
  saleRate?: number;
  mrp?: number;
  minStockLevel?: number;
  articleNo?: string;
  sizeColor?: string;
  imageUrl?: string;
  isActive?: boolean;
  warehouseId: string;
  openingStock?: number;
  itemType?: string;
  procurementType?: 'MAKE' | 'BUY' | 'SUBCONTRACT' | '';
  productionUnit?: string | null;
  standardCost?: number;
  isBatchTracked?: boolean;
  isSerialTracked?: boolean;
  serialPrefix?: string | null;
  defaultWarrantyMonths?: number | null;
  hasExpiry?: boolean;
  shelfLifeDays?: number | null;
  bomId?: string | null;
  bomVersion?: string | null;
  reorderPoint?: number;
  reorderQty?: number;
  leadTimeDays?: number;
  preferredSupplierId?: string | null;
  preferredSubcontractorId?: string | null;
  jobWorkRate?: number;
  scrapRate?: number;
  itemGroup?: string | null;
  drawingNumber?: string | null;
  specifications?: string | null;
  requiresIncomingQC?: boolean;
  requiresFinalQC?: boolean;
  isParent?: boolean;
  hasVariants?: boolean;
}

/** What the API returns (snake_case, matches DB columns) */
export interface ItemResponse {
  id: string;
  company_id: string;
  name: string;
  code: string | null;
  barcode: string | null;
  category_id: string | null;
  category: string | null; // categoryName stored as `category`
  brand: string | null;
  hsn_code: string | null;
  hsn_description?: string | null;
  hsn_verified?: boolean;
  gst_rate: string | number | null; // DB returns numeric as string sometimes
  primary_unit_id: string | null;
  purchase_rate: string | number | null;
  sale_rate: string | number | null;
  unit_name: string | null;
  mrp: string | number | null;
  min_stock_level: string | number | null;
  article_no: string | null;
  size_color: string | null;
  image_url: string | null;
  is_active: boolean;
  created_at: string;
  created_by: string;
  warehouseid?: string | null;
  warehouse_id?: string | null;
  item_type?: string | null;
  procurement_type?: 'MAKE' | 'BUY' | 'SUBCONTRACT' | null;
  production_unit?: string | null;
  standard_cost?: string | number | null;
  is_batch_tracked?: boolean;
  is_serial_tracked?: boolean;
  has_expiry?: boolean;
  shelf_life_days?: number | null;
  bom_id?: string | null;
  bom_version?: string | null;
  reorder_point?: string | number | null;
  reorder_qty?: string | number | null;
  scrap_rate?: string | number | null;
  lead_time_days?: number | null;
  preferred_supplier_id?: string | null;
  preferred_subcontractor_id?: string | null;
  job_work_rate?: string | number | null;
  item_group?: string | null;
  drawing_number?: string | null;
  specifications?: string | null;
  requires_incoming_qc?: boolean;
  requires_final_qc?: boolean;
  is_parent?: boolean;
  has_variants?: boolean;
  variant_count?: number;
  is_variant?: boolean;
  parent_item_id?: string | null;
  variant_name?: string | null;
  variant_sku?: string | null;
  stock?: string | number | null;
  reserved_qty?: string | number | null;
  available_qty?: string | number | null;
  /** Names of what the item links, joined by the item list */
  preferred_supplier_name?: string | null;
  preferred_subcontractor_name?: string | null;
  bom_code?: string | null;
  warehouse_name?: string | null;
}

export interface ApiResponse<T> {
  success: boolean;
  message?: string;
  data?: T;
  count?: number;
}

export interface FilterItemsParams {
  categoryId?: string;
  categoryName?: string;
  search?: string;
  warehouseId?: string;
  stockStatus?: 'ALL' | 'IN_STOCK' | 'LOW_STOCK' | 'OUT_OF_STOCK';
  isActive?: 'ALL' | 'true' | 'false';
}

const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

// ─── Auth helper ──────────────────────────────────────────────────────────────

function getAuthHeaders(): HeadersInit {
  const token = localStorage.getItem('token');
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
}

async function handleResponse<T>(res: Response): Promise<ApiResponse<T>> {
  const data = await res.json();
  if (!res.ok) {
    throw new Error(data?.message || 'Something went wrong');
  }
  return data as ApiResponse<T>;
}

// ─── Mapper: API response → local ItemFormData shape ─────────────────────────

/**
 * DB returns snake_case + numeric fields as strings (Postgres numeric type).
 * This mapper normalises everything for the frontend.
 */
export function mapApiToItem(r: ItemResponse) {
  const row = r as any;
  return {
    id: r.id,
    name: r.name,
    code: r.code ?? '',
    barcode: r.barcode ?? '',
    categoryId: r.category_id ?? '',
    categoryName: r.category ?? '', // DB column is `category`
    brand: r.brand ?? '',
    hsnCode: r.hsn_code ?? '',
    hsnDescription: r.hsn_description ?? null,
    hsnVerified: Boolean(r.hsn_verified),
    taxRate: parseFloat(String(r.gst_rate ?? 18)), // DB column is `gst_rate`
    unitId: r.primary_unit_id ?? '', // DB column is `primary_unit_id`
    unitName: r.unit_short_name ?? r.unit_name ?? '',
    purchaseRate: parseFloat(String(r.purchase_rate ?? 0)),
    saleRate: parseFloat(String(r.sale_rate ?? 0)),
    mrp: parseFloat(String(r.mrp ?? 0)),
    minStockLevel: parseFloat(String(r.min_stock_level ?? 0)),
    articleNo: r.article_no ?? '',
    sizeColor: r.size_color ?? '',
    imageUrl: r.image_url ?? '',
    // stock comes from inventory table, not items — default 0
    stock: (row.stock !== undefined && row.stock !== null) ? parseFloat(String(row.stock)) : 0,
    reservedQty: (row.reserved_qty !== undefined && row.reserved_qty !== null)
      ? parseFloat(String(row.reserved_qty))
      : (row.reserved_stock ? parseFloat(String(row.reserved_stock)) : 0),
    availableQty: (row.available_qty !== undefined && row.available_qty !== null)
      ? parseFloat(String(row.available_qty))
      : Math.max(
          0,
          ((row.stock !== undefined && row.stock !== null) ? parseFloat(String(row.stock)) : 0) -
            ((row.reserved_qty !== undefined && row.reserved_qty !== null)
              ? parseFloat(String(row.reserved_qty))
              : (row.reserved_stock ? parseFloat(String(row.reserved_stock)) : 0))
        ),
    warehouseId: r.warehouseid ?? r.warehouse_id ?? '',
    itemType: r.item_type ?? 'FINISHED_GOOD',
    procurementType: r.procurement_type ?? '',
    productionUnit: r.production_unit ?? null,
    standardCost: parseFloat(String(r.standard_cost ?? 0)),
    isBatchTracked: Boolean(r.is_batch_tracked),
    isSerialTracked: Boolean(r.is_serial_tracked),
    serialPrefix: r.serial_prefix ?? null,
    defaultWarrantyMonths: r.default_warranty_months ?? 12,
    hasExpiry: Boolean(r.has_expiry),
    shelfLifeDays: r.shelf_life_days ?? null,
    bomId: r.bom_id ?? null,
    bomVersion: r.bom_version ?? null,
    reorderPoint: parseFloat(String(r.reorder_point ?? 0)),
    reorderQty: parseFloat(String(r.reorder_qty ?? 0)),
    leadTimeDays: Number(r.lead_time_days ?? 0),
    preferredSupplierId: r.preferred_supplier_id ?? null,
    preferredSubcontractorId: r.preferred_subcontractor_id ?? null,
    preferredSupplierName: r.preferred_supplier_name ?? null,
    preferredSubcontractorName: r.preferred_subcontractor_name ?? null,
    bomCode: r.bom_code ?? null,
    warehouseName: r.warehouse_name ?? null,
    jobWorkRate: parseFloat(String(r.job_work_rate ?? 0)),
    scrapRate: parseFloat(String(r.scrap_rate ?? 0)),
    itemGroup: r.item_group ?? null,
    drawingNumber: r.drawing_number ?? null,
    specifications: r.specifications ?? null,
    requiresIncomingQC: Boolean(r.requires_incoming_qc),
    requiresFinalQC: Boolean(r.requires_final_qc),
    isParent: Boolean(r.is_parent),
    hasVariants: Boolean(r.has_variants),
    variantCount: Number(r.variant_count ?? 0),
    isVariant: Boolean(r.is_variant),
    parentItemId: r.parent_item_id ?? null,
    variantName: r.variant_name ?? null,
    variantSku: r.variant_sku ?? null,
    variantAttributes: (r as any).variant_attributes ?? null,
    variantAttributeNames: (r as any).variant_attribute_names ?? [],
    isActive: r.is_active !== undefined ? Boolean(r.is_active) : (row.isActive !== undefined ? Boolean(row.isActive) : true),
  };
}

// ─── API Calls ────────────────────────────────────────────────────────────────

/** POST /api/v1/item */
export async function createItem(
  payload: ItemPayload,
): Promise<ApiResponse<ItemResponse>> {
  const res = await fetch(`${BASE_URL}/item`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(payload),
  });
  return handleResponse<ItemResponse>(res);
}

/** PUT /api/v1/item/:id */
export async function updateItem(
  id: string,
  payload: Partial<ItemPayload>,
): Promise<ApiResponse<ItemResponse>> {
  const res = await fetch(`${BASE_URL}/item/${id}`, {
    method: 'PUT',
    headers: getAuthHeaders(),
    body: JSON.stringify(payload),
  });
  return handleResponse<ItemResponse>(res);
}

/**
 * DELETE /api/v1/item/:id
 * Controller does a SOFT DELETE (sets is_active = false)
 */
export async function deleteItem(id: string): Promise<ApiResponse<null>> {
  try {
    const res = await fetch(`${BASE_URL}/item/${id}`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
    });

    const data = await res.json();

    // Agar 409 (Conflict - Foreign Key Violation) hai
    if (res.status === 409) {
      throw new Error(
        data?.message || 'Cannot delete item - it is being used in GRN',
      );
    }

    // Agar 404 ya koi aur error hai
    if (!res.ok) {
      throw new Error(data?.message || 'Something went wrong');
    }

    return data as ApiResponse<null>;
  } catch (error) {
    // Error ko re-throw karo taaki component catch kar sake
    throw error;
  }
}

/** GET /api/v1/item — returns only is_active = true items */
export async function getAllItems(warehouseId?: string): Promise<ApiResponse<ItemResponse[]>> {
  const query = warehouseId && warehouseId !== 'ALL' ? `?warehouse_id=${encodeURIComponent(warehouseId)}` : '';
  const res = await fetch(`${BASE_URL}/item${query}`, {
    method: 'GET',
    headers: getAuthHeaders(),
  });
  return handleResponse<ItemResponse[]>(res);
}

/**
 * GET /api/v1/item/filter?categoryName=Electronics&search=laptop&categoryId=uuid
 *
 * Matches the backend filterItems controller which reads from req.query.
 * All params are optional — omitting them returns all active items.
 */
export async function filterItems(
  params: FilterItemsParams,
): Promise<ApiResponse<ItemResponse[]>> {
  console.log('FILTER PARAMS:', params);
  const query = new URLSearchParams();

  if (params.categoryId && params.categoryId !== 'ALL') {
    query.set('categoryId', params.categoryId);
  }
  if (params.categoryName && params.categoryName !== 'ALL') {
    query.set('categoryName', params.categoryName);
  }
  if (params.search) {
    query.set('search', params.search);
  }
  if (params.stockStatus && params.stockStatus !== 'ALL') {
    query.set('stockStatus', params.stockStatus);
  }
  if (params.isActive && params.isActive !== 'ALL') {
    query.set('isActive', params.isActive);
  }
  if (
    params.warehouseId &&
    params.warehouseId !== 'ALL' &&
    UUID_RE.test(params.warehouseId)
  ) {
    query.set('warehouseId', params.warehouseId);
  }

  const qs = query.toString();
  const url = `${BASE_URL}/item/filter${qs ? `?${qs}` : ''}`;
  console.log(
    'REQUEST URL:',
    `${BASE_URL}/item/filter${query.toString() ? `?${query.toString()}` : ''}`,
  );
  const res = await fetch(url, {
    method: 'GET',
    headers: getAuthHeaders(),
  });
  return handleResponse<ItemResponse[]>(res);
}

export async function searchItemByBarcode(barcode: string) {
  const res = await fetch(`${BASE_URL}/item?search=${barcode}`, {
    method: 'GET',
    headers: getAuthHeaders(),
  });

  const data = await handleResponse<ItemResponse[]>(res);

  return data.data?.[0] || null;
}

export interface BOMDropdownVariant {
  id: string;
  name: string;
  code?: string;
  item_type?: string;
  variant_name?: string;
  parent_item_name?: string;
  parent_item_id?: string;
  purchase_rate?: number;
  unit_name?: string;
}

export interface BOMDropdownGroup {
  id: string;
  name: string;
  code?: string;
  item_type?: string;
  category?: string;
  purchase_rate?: number;
  unit_name?: string;
  variants: BOMDropdownVariant[];
}

export async function getItemsWithVariantsForBOM(warehouseId?: string): Promise<BOMDropdownGroup[]> {
  const query = warehouseId && warehouseId !== 'ALL' ? `?warehouse_id=${encodeURIComponent(warehouseId)}` : '';
  const res = await fetch(`${BASE_URL}/item/bom-dropdown${query}`, {
    method: 'GET',
    headers: getAuthHeaders(),
  });
  const data = await handleResponse<BOMDropdownGroup[]>(res);
  return data.data || [];
}

/** POST /api/v1/item/:id/bulk-variants */
export async function createBulkVariants(
  parentId: string,
  payload: { attributeNames: string[]; variants: any[] }
): Promise<ApiResponse<any>> {
  const res = await fetch(`${BASE_URL}/item/${parentId}/bulk-variants`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(payload),
  });
  return handleResponse<any>(res);
}

/** GET /api/v1/item/:id/variants */
export async function getVariantsByParentId(
  parentId: string
): Promise<ApiResponse<any[]>> {
  const res = await fetch(`${BASE_URL}/item/${parentId}/variants`, {
    method: 'GET',
    headers: getAuthHeaders(),
  });
  return handleResponse<any[]>(res);
}

/** POST /api/v1/item/variants */
export async function createSingleVariant(
  payload: any
): Promise<ApiResponse<any>> {
  const res = await fetch(`${BASE_URL}/item/variants`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(payload),
  });
  return handleResponse<any>(res);
}

/** PUT /api/v1/item/variants/:id */
export async function updateVariant(
  id: string,
  payload: any
): Promise<ApiResponse<any>> {
  const res = await fetch(`${BASE_URL}/item/variants/${id}`, {
    method: 'PUT',
    headers: getAuthHeaders(),
    body: JSON.stringify(payload),
  });
  return handleResponse<any>(res);
}

// ─── Reservations & Allocations ──────────────────────────────────────────────

export interface ItemReservationDetail {
  id: string;
  production_order_id: string;
  po_number: string;
  manufactured_product: string;
  po_status: string;
  target_date?: string | null;
  actual_start_date?: string | null;
  required_qty: number;
  reserved_qty: number;
  issued_qty: number;
  unit: string;
  reservation_status: string;
  created_at: string;
  warehouse_name?: string | null;
}

export interface ItemSalesAllocation {
  id: string;
  so_id: string;
  so_number: string;
  customer_name: string;
  so_status: string;
  delivery_date?: string | null;
  ordered_qty: number;
  delivered_qty: number;
  pending_qty: number;
  created_at: string;
}

export interface ItemReservationsResponse {
  item: {
    id: string;
    name: string;
    code?: string;
    brand?: string;
    unit_name: string;
    stock: number;
    totalReserved: number;
    availableQty: number;
  };
  summary: {
    totalPoReserved: number;
    totalSoPending: number;
    totalReservations: number;
  };
  productionReservations: ItemReservationDetail[];
  salesAllocations: ItemSalesAllocation[];
}

/** GET /api/v1/item/:id/reservations */
export async function getItemReservations(
  itemId: string
): Promise<ApiResponse<ItemReservationsResponse>> {
  const res = await fetch(`${BASE_URL}/item/${itemId}/reservations`, {
    method: 'GET',
    headers: getAuthHeaders(),
  });
  return handleResponse<ItemReservationsResponse>(res);
}